/**
 * CSAM image descriptions are a representation of the flagged media. They show
 * up in two places: ``contentModeration.imageDescription`` on file-info, and
 * the citation chunk rendered under View Details (often "This image shows…").
 * Callers must use these helpers instead of rendering either string directly.
 */

export type ContentModerationGate = {
  type?: string | null;
  findingStatus?: string | null;
  imageDescription?: string | null;
} | null | undefined;

/** Active finding. A ``REVIEWED`` finding is cleared and is not flagged content. */
export function isActiveContentModeration(moderation: ContentModerationGate): boolean {
  const type = moderation?.type?.trim();
  if (!type) return false;
  return moderation?.findingStatus !== 'REVIEWED';
}

/**
 * Citation text safe to render in View Details.
 * Returns null when the chunk must be withheld (flagged file, viewer cannot
 * see descriptions) or when there is nothing to show.
 */
export function citationForViewer(
  citation: string | null | undefined,
  canSeeDescriptions: boolean,
  moderation: ContentModerationGate,
): string | null {
  if (citation == null || citation.trim() === '') return null;
  if (!canSeeDescriptions && isActiveContentModeration(moderation)) return null;
  return citation;
}

/**
 * File summary safe to hand to child viewers. Drops ``imageDescription`` when
 * the viewer cannot see CSAM descriptions so the string is not copied into
 * captures or other downstream state.
 */
export function fileSummaryForViewer<
  T extends { contentModeration?: { imageDescription?: string | null } | null },
>(fileInfo: T | null | undefined, canSeeDescriptions: boolean): T | null | undefined {
  if (!fileInfo || canSeeDescriptions) return fileInfo;
  if (!fileInfo.contentModeration?.imageDescription) return fileInfo;
  return {
    ...fileInfo,
    contentModeration: {
      ...fileInfo.contentModeration,
      imageDescription: null,
    },
  };
}
